import { useEffect, useState } from "react";
import { Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function parseLine(line) {
  if (!line || /closed/i.test(line)) return null;
  const m = line.match(/:\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*[–—-]\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
  if (!m) return null;
  const toMin = (h, mm, ap) => ((parseInt(h, 10) % 12) + (/pm/i.test(ap) ? 12 : 0)) * 60 + parseInt(mm || "0", 10);
  const label = (h, mm, ap) => `${h}${mm ? `:${mm}` : ""} ${ap.toUpperCase()}`;
  return { open: toMin(m[1], m[2], m[3]), close: toMin(m[4], m[5], m[6]), openLabel: label(m[1], m[2], m[3]) };
}

function anyParseable(hours) {
  return hours.some((h) => parseLine(h) !== null);
}

function getSydneyNow() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Australia/Sydney",
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const dayIdx = DAYS.indexOf(get("weekday"));
  const mins = (parseInt(get("hour"), 10) % 24) * 60 + parseInt(get("minute"), 10);
  return { dayIdx, mins };
}

function computeStatus(hours) {
  if (!anyParseable(hours)) return null;
  const { dayIdx: today, mins } = getSydneyNow();
  const todayRange = parseLine(hours[today]);
  if (todayRange && mins >= todayRange.open && mins < todayRange.close) {
    return { open: true, label: "Open now — call Dave" };
  }
  if (todayRange && mins < todayRange.open) {
    return { open: false, label: `Closed · opens ${todayRange.openLabel} today` };
  }
  for (let i = 1; i <= 7; i++) {
    const d = (today + i) % 7;
    const range = parseLine(hours[d]);
    if (range) {
      return { open: false, label: `Closed · opens ${range.openLabel} ${i === 1 ? "tomorrow" : DAYS[d].slice(0, 3)}` };
    }
  }
  return { open: false, label: "Closed" };
}

export function computeClosedToday(hours) {
  if (!anyParseable(hours)) return { closedAllDay: false, nextLabel: null };
  const { dayIdx: today } = getSydneyNow();
  if (parseLine(hours[today])) return { closedAllDay: false, nextLabel: null };
  for (let i = 1; i <= 7; i++) {
    const d = (today + i) % 7;
    const range = parseLine(hours[d]);
    if (range) {
      return { closedAllDay: true, nextLabel: `${range.openLabel} ${i === 1 ? "tomorrow" : DAYS[d]}` };
    }
  }
  return { closedAllDay: true, nextLabel: null };
}

export default function OpenNowBadge({ className = "" }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let timer;
    fetch(`${API}/business`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && Array.isArray(d.hours) && d.hours.length) {
          const update = () => setStatus(computeStatus(d.hours));
          update();
          timer = setInterval(update, 60000);
        }
      })
      .catch(() => {});
    return () => clearInterval(timer);
  }, []);

  if (!status) return null;

  return (
    <a
      data-testid="open-now-badge"
      href="tel:+61408464106"
      className={`group flex items-center gap-2.5 rounded-full border px-4 py-2.5 backdrop-blur-md transition-colors duration-300 ${
        status.open
          ? "border-[#1E9E5A]/60 bg-[#122A1F]/80 hover:border-[#1E9E5A]"
          : "border-white/15 bg-[#122A1F]/80 hover:border-[#F0A829]/60"
      } ${className}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
            status.open ? "bg-[#1E9E5A]" : "bg-[#F0A829]"
          }`}
        />
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${status.open ? "bg-[#1E9E5A]" : "bg-[#F0A829]"}`}
        />
      </span>
      <span
        className={`font-display text-xs font-bold uppercase tracking-wider ${
          status.open ? "text-[#1E9E5A]" : "text-[#B9C4B4]"
        }`}
      >
        {status.label}
      </span>
      {status.open && (
        <Phone className="h-3.5 w-3.5 text-[#1E9E5A] transition-transform duration-300 group-hover:rotate-12" strokeWidth={2.5} />
      )}
    </a>
  );
}
